import type { CurrencyCode } from '../statuses';

/**
 * Money is represented as **integer minor units** (e.g. USD cents). Never store or
 * transport monetary amounts as floats. USD is the base pricing currency; VES is
 * for display and local payment only.
 */
export type MinorUnits = number;

export const CURRENCY_DECIMALS: Record<CurrencyCode, number> = { USD: 2, VES: 2 };

/** Convert a human decimal amount (e.g. 1500.5) to integer minor units. */
export function toMinorUnits(amount: number, currency: CurrencyCode = 'USD'): MinorUnits {
  const factor = 10 ** CURRENCY_DECIMALS[currency];
  return Math.round(amount * factor);
}

/** Convert integer minor units back to a decimal number for display/formatting. */
export function fromMinorUnits(minor: MinorUnits, currency: CurrencyCode = 'USD'): number {
  const factor = 10 ** CURRENCY_DECIMALS[currency];
  return minor / factor;
}

/** Locale-aware currency formatting. Defaults to Venezuelan Spanish. */
export function formatMoney(minor: MinorUnits, currency: CurrencyCode = 'USD', locale = 'es-VE'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    fromMinorUnits(minor, currency),
  );
}

/**
 * An exchange rate captured at a point in time. `rate` = VES per 1 USD.
 * Once locked on an order (or recorded on a payment) it is IMMUTABLE — historical
 * documents must never be recomputed when the live rate changes.
 */
export interface ExchangeRate {
  base: 'USD';
  quote: 'VES';
  rate: number;
  capturedAt: string;
}

/** Convert a USD minor amount to VES minor units using a locked rate. */
export function convertUsdMinorToVesMinor(usdMinor: MinorUnits, rate: number): MinorUnits {
  return toMinorUnits(fromMinorUnits(usdMinor, 'USD') * rate, 'VES');
}

/** Sum a list of minor-unit values (integer-safe). */
export function sumMinor(values: readonly MinorUnits[]): MinorUnits {
  return values.reduce((acc, v) => acc + v, 0);
}

/** Line total in minor units. Quantity is truncated to a whole unit count. */
export function lineTotalMinor(unitPriceMinor: MinorUnits, quantity: number): MinorUnits {
  return unitPriceMinor * Math.trunc(quantity);
}
